import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import GameMenu from "../components/GameMenu";
import Timer from "../components/Timer";
import Popup from "../components/Popup";
import LeaveGamePopup from "../components/LeaveGamePopup";
import GameWrapper from "../components/GameCountdown";
import "../assets/css/arrowsQCM.css";
import { arrowsDir } from '../assets/images/arrowQCM';
import api from "../api/client";

export default function ArrowsQCM() {
  const [gameStarted, setGameStarted] = useState(false);

  const location = useLocation();
  const { theme, subTheme, level = "B1" } = location.state || {};

  const [timerIsFinished, setTimerIsFinished] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [leaveGamePopup, setLeaveGamePopup] = useState(false);
  const getTimerTimeRef = useRef(null);

  // ── Chargement depuis l'API ──────────────────────────────────────
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [translations, setTranslations] = useState({});
  const [hoveredAnswer, setHoveredAnswer] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams({ level, n: 10 });
    if (theme) params.append("theme", theme);
    if (subTheme) params.append("sous_theme", subTheme);

    api.get(`/api/games/mcq/?${params}`)
      .then(({ data }) => {
        const formatted = data.items.map((item) => ({
          question: item.question,
          options: item.choices,
          correctAnswer: item.answer,
        }));
        setQuestions(formatted);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [theme, subTheme, level]);

  useEffect(() => {
    if (!questions.length) return;
    const fetchTranslations = async () => {
      try {
        const allWords = [
          ...new Set(
            questions.flatMap(q => q.options)
          )
        ];
        const results = await Promise.all(
          allWords.map(word =>
            api.get(`/api/lexicon/word/?term=${encodeURIComponent(word)}`)
              .then(res => ({ word, data: res.data }))
              .catch(() => ({ word, data: null }))
          )
        );
        const translationsMap = {};
        results.forEach(({ word, data }) => {
          translationsMap[word] = data?.results?.[0]?.translation_fr || null;
        });
        setTranslations(translationsMap);
      } catch (err) {
        console.error("Erreur traduction :", err);
      }
    };
    fetchTranslations();
  }, [questions]);
  // ────────────────────────────────────────────────────────────────

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [holdingAnswer, setHoldingAnswer] = useState(null);
  const holdTimerRef = useRef(null);

  const currentQuestion = questions[currentIndex];

  const sendTime = (time) => setTimeElapsed(time);
  const sendLeaveGame = (value) => setLeaveGamePopup(value);

  const handleAnswerClick = (answer) => {
    if (!selectedAnswer && currentQuestion) {
      setSelectedAnswer(answer);
      if (answer === currentQuestion.correctAnswer) {
        setScore((prev) => prev + 1);
      }
      setTimeout(() => nextQuestion(), 3000);
    }
  };

  const nextQuestion = () => {
    if (currentIndex + 1 < questions.length) {
      setCurrentIndex((prev) => prev + 1);
      setSelectedAnswer(null);
    } else {
      endGame();
    }
  };

  const getButtonClass = (answer) => {
    if (holdingAnswer === answer && !selectedAnswer) return "holding";
    if (!selectedAnswer) return "";
    if (answer === currentQuestion.correctAnswer) return "correct";
    if (answer === selectedAnswer) return "wrong";
    return "";
  };


  // ── Enregistrement session ────────────────────────────────────────
  const saveSession = async (timeSeconds) => {
    try {
      await api.post('/api/gameplay/session/', {
        game_code:    'arrows_qcm',
        theme:        theme,
        sous_theme:   subTheme,
        level:        level,
        score:        score,
        total:        questions.length,
        correct:      score,
        wrong:        questions.length - score,
        time_seconds: timeSeconds || timeElapsed,
      });
    } catch (e) {
      console.warn('Session non enregistree :', e);
    }
  };

  const endGame = async () => {
    const t = getTimerTimeRef.current ? getTimerTimeRef.current() : timeElapsed;
    sendTime(t);
    await saveSession(t);
    setFinished(true);
    setTimerIsFinished(true);
  };

  // Gestion des flèches avec maintien
  useEffect(() => {
    if (!currentQuestion) return;

    const keyMap = { ArrowUp: 0, ArrowRight: 1, ArrowDown: 2, ArrowLeft: 3 };

    const handleKeyDown = (e) => {
      if (finished || selectedAnswer) return;
      if (!(e.key in keyMap)) return;
      const answer = currentQuestion.options[keyMap[e.key]];
      if (!holdTimerRef.current) {
        setHoldingAnswer(answer);
        holdTimerRef.current = setTimeout(() => {
          handleAnswerClick(answer);
          setHoldingAnswer(null);
          holdTimerRef.current = null;
        }, 1000);
      }
    };

    const handleKeyUp = () => {
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
        setHoldingAnswer(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [currentQuestion, selectedAnswer, finished]);

  return (
    <div className="page-container">
      <Sidebar />

      {loading ? (
        <div className="content-container inGame" id="arrowsQCM">
          <div className="loading-container">
              <h1>Chargement...</h1>
          </div>
        </div>
      ) : error ? (
        <div className="content-container inGame" id="arrowsQCM">
          <div className="error-container">
              <h2>Une erreur est survenue lors du chargement des données.</h2>
              {/* <p>{apiError}</p> */}
              <button className="mode-btn active" onClick={() => window.location.reload()}>
                  Réessayer
              </button>
          </div>
        </div>
      ) : (
        <GameWrapper onStart={() => setGameStarted(true)}>
          <div className="content-container inGame" id="arrowsQCM">
            <div className="title">
              <h4>{theme} - {subTheme}</h4>
              <div className="level-infos">
                <Timer
                  timer={timerIsFinished}
                  onFinish={() => endGame()}
                  onSendTime={sendTime}
                  onGetTime={(getTimeFn) => (getTimerTimeRef.current = getTimeFn)}
                  hasLimit={true}
                  limit={120}
                />
              </div>
            </div>

            <div className="game-container launched">
              {currentQuestion ? (
                <>
                  <div className="arrows-info">
                    <div className="arrows-length">
                      <p>Question {currentIndex + 1}/{questions.length}</p>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                    <h3 className="arrows-score">Score : {score}/{questions.length}</h3>
                  </div>
                  <div className="arrows-QR">
                    <h2 className="arrows-question">{currentQuestion.question}</h2>
                    <h3 className="arrows-rules">Maintenez la flèche correspondante enfoncée pour valider une réponse.</h3>
                  </div>
                  <div className="arrows-options">
                    {currentQuestion.options.map((answer, index) => (
                      <button
                        key={index}
                        className={`arrows-button option${index} ${getButtonClass(answer)}`}
                        onClick={(e) => e.preventDefault()}
                        onMouseEnter={() => setHoveredAnswer(answer)}
                        onMouseLeave={() => setHoveredAnswer(null)}
                      >
                        <span>
                          {hoveredAnswer === answer && translations[answer]
                            ? translations[answer]
                            : answer}
                        </span>
                      </button>
                    ))}
                    <div className="arrows-hint">
                      {/* <div className="up">🠑</div>
                      <div className="right">🠒</div>
                      <div className="down">🠓</div>
                      <div className="left">🠐</div> */}
                      <div className="up">
                        <img src={arrowsDir.up} />
                      </div>
                      <div className="right">
                        <img src={arrowsDir.right} />
                      </div>
                      <div className="down">
                        <img src={arrowsDir.down} />
                      </div>
                      <div className="left">
                        <img src={arrowsDir.left} />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <h3 className="arrows-score">Score final : {score}/{questions.length}</h3>
              )}
            </div>
          </div>
        </GameWrapper>
      )}

      <GameMenu onSendLeaveGame={sendLeaveGame} />
      <Popup time={timeElapsed} timerIsFinished={timerIsFinished} errorCount={questions.length - score} />
      <LeaveGamePopup popupOpen={leaveGamePopup} onSendLeaveGame={sendLeaveGame} />
    </div>
  );
}